import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Scale,
  Target,
  Waypoints,
  Server,
  FileText,
  ListChecks,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { useDecisions } from '../../features/decisions/useDecisions';
import {
  formatDate,
  isRevisitDue,
  revisitInDays,
} from '../../features/decisions/decisionModel';
import type { Decision } from '../../features/decisions/types';
import { DecisionModal } from './DecisionModal';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold text-zinc-100">{title}</h2>
      {children}
    </div>
  );
}

/** Decision detail — the full record, its links, and revisit schedule. */
export function DecisionDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    decisions,
    goals,
    workstreams,
    tasks,
    services,
    documents,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    documentTitles,
    loading,
    error,
    get,
    update,
    remove,
  } = useDecisions();

  const decision = useMemo(() => decisions.find((d) => d.id === id), [decisions, id]);
  const [editing, setEditing] = useState<Decision | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openEdit = async () => {
    const full = await get(id);
    if (!full) {
      toast({ tone: 'error', title: 'Not found', description: 'Decision could not be loaded.' });
      return;
    }
    setEditing(full);
    setModalOpen(true);
  };

  const onSubmit = async (draft: Parameters<typeof update>[1]) => {
    await update(id, draft);
    toast({ tone: 'success', title: 'Decision updated', description: draft.decision });
  };
  const doDelete = async () => {
    if (!decision) return;
    setDeleting(true);
    try {
      await remove(decision.id);
      toast({ tone: 'success', title: 'Decision removed', description: decision.decision });
      navigate('/decisions');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove decision',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading decision" />
      </div>
    );
  }
  if (error) {
    return <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load decision" description={error} />;
  }
  if (!decision) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Decision not found"
        description="It may have been removed."
        action={<Button onClick={() => navigate('/decisions')}>Back to Decision Log</Button>}
      />
    );
  }

  const revisitDays = revisitInDays(decision);
  const due = isRevisitDue(decision);

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/decisions')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-zinc-200"
      >
        <ArrowLeft className="h-4 w-4" /> Decision Log
      </button>

      <PageHeader
        icon={<Scale className="h-5 w-5" />}
        title={decision.decision || 'Untitled decision'}
        description={decision.context}
        actions={
          <>
            <Button variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={openEdit}>
              Edit
            </Button>
            <Button variant="ghost" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmingDelete(true)}>
              Delete
            </Button>
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
        {decision.owner && (
          <span>Owner: <span className="text-zinc-300">{decision.owner}</span></span>
        )}
        {decision.date && <span>· {formatDate(decision.date)}</span>}
        {decision.revisitDate && (
          <Badge tone={due ? 'danger' : 'info'}>
            <CalendarClock className="h-3 w-3" />
            {due ? `revisit overdue ${Math.abs(revisitDays ?? 0)}d` : `revisit ${formatDate(decision.revisitDate)}`}
          </Badge>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardBody className="space-y-4">
              {decision.decisionMade && (
                <Section title="Decision made">
                  <p className="whitespace-pre-wrap text-sm text-zinc-200">{decision.decisionMade}</p>
                </Section>
              )}
              {decision.why && (
                <Section title="Why">
                  <p className="whitespace-pre-wrap text-sm text-zinc-300">{decision.why}</p>
                </Section>
              )}
              {decision.optionsConsidered.length > 0 && (
                <Section title="Options considered">
                  <ul className="list-inside list-disc space-y-1 text-sm text-zinc-300">
                    {decision.optionsConsidered.map((o, i) => (
                      <li key={`${i}:${o}`}>{o}</li>
                    ))}
                  </ul>
                </Section>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Section title="Relationships">
                <div className="flex flex-wrap items-center gap-1.5">
                  {decision.relatedGoalId && (
                    <button type="button" onClick={() => navigate(`/goals/${encodeURIComponent(decision.relatedGoalId!)}`)}>
                      <Badge tone="accent"><Target className="h-3 w-3" /> {goalTitles.get(decision.relatedGoalId) ?? 'Goal'}</Badge>
                    </button>
                  )}
                  {decision.relatedWorkstreamId && (
                    <button type="button" onClick={() => navigate(`/workstreams/${encodeURIComponent(decision.relatedWorkstreamId!)}`)}>
                      <Badge tone="info"><Waypoints className="h-3 w-3" /> {workstreamTitles.get(decision.relatedWorkstreamId) ?? 'Workstream'}</Badge>
                    </button>
                  )}
                  {decision.relatedDocId && (
                    <button type="button" onClick={() => navigate(`/documents/${encodeURIComponent(decision.relatedDocId!)}`)}>
                      <Badge tone="neutral"><FileText className="h-3 w-3" /> {documentTitles.get(decision.relatedDocId) ?? 'Document'}</Badge>
                    </button>
                  )}
                  {decision.affectedServices.map((s) => (
                    <button key={s} type="button" onClick={() => navigate(`/services/${encodeURIComponent(s)}`)}>
                      <Badge tone="neutral"><Server className="h-3 w-3" /> {serviceTitles.get(s) ?? s}</Badge>
                    </button>
                  ))}
                  {decision.relatedStoryIds.map((s) => (
                    <Badge key={s} tone="neutral"><ListChecks className="h-3 w-3" /> {storyTitles.get(s) ?? s}</Badge>
                  ))}
                  {!decision.relatedGoalId &&
                    !decision.relatedWorkstreamId &&
                    !decision.relatedDocId &&
                    decision.affectedServices.length === 0 &&
                    decision.relatedStoryIds.length === 0 && (
                      <span className="text-sm text-zinc-500">No linked entities.</span>
                    )}
                </div>
              </Section>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardBody className="space-y-3 text-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Participants</p>
                <p className="text-zinc-200">
                  {decision.participants.length ? decision.participants.join(', ') : '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Recorded by</p>
                <p className="text-zinc-200">{decision.createdBy || '—'}</p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <DecisionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultOwner={currentAlias}
        initialDecision={editing}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        services={services}
        documents={documents}
        serviceTitles={serviceTitles}
        storyTitles={storyTitles}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={confirmingDelete}
        title="Remove decision?"
        confirmLabel="Remove decision"
        loading={deleting}
        onConfirm={() => void doDelete()}
        onClose={() => setConfirmingDelete(false)}
        message="Remove this decision from the log? This can't be undone."
      />
    </div>
  );
}

export default DecisionDetailPage;
